import { useState } from "react";
import { Field, SearchBox, Body1, Checkbox } from "@fluentui/react-components";
import { useQuery } from "@tanstack/react-query";
import type { Result } from "@pistonite/pure/result";
import { useDebounce } from "@uidotdev/usehooks";

import type { ItemSearchResult } from "@pistonite/skybook-api";
import { useUITranslation } from "skybook-localization";
import { type CookEffect, StandaloneItemSlot } from "@pistonite/skybook-itemsys";

import { Code, Interpolate } from "#ui/components";
import { useStyleEngine } from "#util";

export type Searcher = {
    search(localized: boolean, query: string): Promise<Result<ItemSearchResult[], string>>;
};

const useStyles = useStyleEngine.extend({
    "results-scroll": {
        marginTop: "8px",
    },
});

type ItemExplorerProps = {
    searcher: Searcher;
    cheap?: boolean;
    disableAnimation?: boolean;
};

export const ItemExplorer: React.FC<ItemExplorerProps> = ({
    searcher,
    cheap,
    disableAnimation,
}) => {
    const [value, setValue] = useState("");
    const [localized, setLocalized] = useState(false);

    const deferredValue = useDebounce(value, 200);

    const { data } = useQuery({
        queryKey: ["item-explorer-search", localized, deferredValue],
        queryFn: () => searcher.search(localized, deferredValue),
    });

    const error = data?.err;
    const results = data?.val;
    const hasResults = results !== undefined && results.length > 0;

    const m = useStyles();
    const t = useUITranslation();

    const $SearchBox = (
        <Field validationState={error ? "error" : "none"} validationMessage={error}>
            <SearchBox
                placeholder={t("item_explorer.label.search_placeholder")}
                value={value}
                onChange={(_, { value }) => {
                    setValue(value);
                }}
            />
        </Field>
    );
    const $LocalizedCheckbox = (
        <Field>
            <Checkbox
                label={t("item_explorer.label.search_localized")}
                checked={localized}
                onChange={(_, { checked }) => {
                    setLocalized(!!checked);
                }}
            />
        </Field>
    );

    const $SearchTip = localized ? (
        <Interpolate quote={<Code>{`"`}</Code>} code_example={<Code>{"fr:espadon royal"}</Code>}>
            {t("item_explorer.desc.search_tip_localized")}
        </Interpolate>
    ) : (
        <Interpolate hyphen={<Code>{"-"}</Code>} example={<Code>{"royal-claymore"}</Code>}>
            {t("item_explorer.desc.search_tip_ident")}
        </Interpolate>
    );

    const $Results = hasResults && (
        <div className={m("overflow-y-auto flex-1 c-results-scroll")}>
            <div className={m("flex flex-wrap max-h-0 overflow-visible pad-itemtop")}>
                {results.map(({ actor, cookEffect }, i) => (
                    <StandaloneItemSlot
                        tooltip
                        key={i}
                        actor={actor}
                        effect={cookEffect as CookEffect}
                        cheap={cheap}
                        disableAnimation={disableAnimation}
                        dragData={{
                            type: "search",
                            localized,
                            payload: {
                                actor,
                                cookEffect,
                            },
                            isMasterSwordFullPower: false,
                        }}
                    />
                ))}
            </div>
        </div>
    );

    return (
        <div className={m("flex-col h-100 border-box pad-8 overflow-auto")}>
            {$SearchBox}
            {$LocalizedCheckbox}
            <Body1 block>{$SearchTip}</Body1>
            {$Results}
            {!hasResults && !!value && (
                <div className={m("flex flex-1 flex-center")}>
                    <Body1>{t("item_explorer.label.no_results")}</Body1>
                </div>
            )}
        </div>
    );
};
